'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, CheckCircle, Lightbulb } from 'lucide-react'

export default function MaskPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-green-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/equipment"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          장비 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              프리다이빙 마스크 선택법
            </h1>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 마스크의 중요성 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                마스크의 중요성
              </h2>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                프리다이빙에서 마스크는 가장 중요한 장비 중 하나입니다. 수압에 의한 압박을 최소화하고
                수중 시야를 확보하는 역할을 합니다.
              </p>
            </div>

            {/* 선택 기준 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                선택 기준
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { num: '1️⃣', title: '내부 부피', desc: '작을수록 좋음 (이퀄라이징 용이)' },
                  { num: '2️⃣', title: '밀착성', desc: '얼굴에 잘 맞아야 물이 안 들어옴' },
                  { num: '3️⃣', title: '시야', desc: '넓은 시야각 확보' },
                  { num: '4️⃣', title: '재질', desc: '부드러운 실리콘 소재' },
                ].map((item, idx) => (
                  <div key={idx} className={`flex items-start gap-2 p-3 xxs:p-2 rounded-lg ${
                    isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                  }`}>
                    <span className="text-lg">{item.num}</span>
                    <div>
                      <span className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {item.title}
                      </span>
                      <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        : {item.desc}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 피팅 테스트 방법 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                피팅 테스트 방법
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  '마스크를 얼굴에 대고 숨을 들이마십니다',
                  '손을 떼어도 마스크가 떨어지지 않아야 합니다',
                  '코로 숨을 내쉬어 이퀄라이징이 잘 되는지 확인',
                  '불편한 압박감이 없어야 합니다',
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className={`w-5 h-5 xxs:w-4 xxs:h-4 rounded-full flex items-center justify-center text-xs xxs:text-[10px] font-bold ${
                      isDark ? 'bg-green-500 text-black' : 'bg-green-500 text-white'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 추천 브랜드 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                추천 브랜드
              </h2>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Cressi, Mares, Omer, Salvimar 등의 전문 브랜드가 인기가 많습니다.
                가격대는 5만원~15만원 정도입니다.
              </p>
            </div>

            {/* 구매 팁 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                    구매 팁
                  </h3>
                  <p className={`text-sm xxs:text-xs ${isDark ? 'text-blue-200/80' : 'text-blue-700'}`}>
                    온라인 구매보다는 매장에서 직접 착용해보고 구매하는 것을 추천합니다.
                    얼굴 형태마다 맞는 마스크가 다르기 때문입니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
